import React from 'react';
import LcaLayout from 'src/Lca/components/layout';
import Breadcrumbs from 'src/Lca/components/Breadcrumbs';
import { useTranslation } from 'react-i18next';
import {
  ProductsItemsCreated,
  ProductsItemsEmpty,
} from 'src/Lca/components/productsItems';
import PageLoading from 'src/components/PageLoading';
import { useElementsPagination, useLcaOrganization } from 'src/common/hooks';
import {
  LcaFolderFilter,
  LcaProductFolderOutSchema,
  LcaProductKind,
  LcaProductOrdering,
} from '../types';
import {
  Typography,
  InputAdornment,
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  Box,
  MenuItem as SelectItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { ArrowDropDown, Search } from '@mui/icons-material';
import { toast } from 'react-toastify';
import ElementsPagination from 'src/components/Pagination';
import ConfirmationModal from 'src/components/ConfirmationModal';
import {
  useLcaProductBulkActions,
  useLcaProductFolderActions,
  useLcaProductFolders,
  useLcaProductFoldersSummary,
  useLcaProducts,
} from '../hooks';
import DelayedTextField from 'src/components/DelayedTextField';
import { useNavigate } from 'react-router-dom';
import ProductExcelUploadDialog from './ProductExcelUploadDialog';
import NewProductExcelImportDialog from './NewProductExcelImportDialog';
import LcaSettingsDialog from './LcaSettingsDialog';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AreaSelector from '../components/AreaSelector';
import { useProductsEmissions } from '../hooks/useProductsEmissions';
import FolderNameDialog from './FolderNameDialog';
import FolderPath from './FolderPath';
import FoldersSidebar from './FoldersSidebar';
import MoveToFolderDialog from './MoveToFolderDialog';
import ProductSelectionBar from './ProductSelectionBar';
import {
  formatFolderPath,
  getDeleteNameClashes,
  getFolderPath,
  getSubtreeIds,
} from './folderTree';

const PRODUCT_KINDS: LcaProductKind[] = ['all', 'finished', 'semi_finished'];

const KIND_LABEL_KEYS: Record<LcaProductKind, string> = {
  all: 'productFilters.kind.all',
  finished: 'productFilters.kind.finished',
  semi_finished: 'productFilters.kind.semiFinished',
};

const ORDERING_LABEL_KEYS: Record<LcaProductOrdering, string> = {
  recently_updated: 'productFilters.ordering.recentlyUpdated',
  name_asc: 'productFilters.ordering.nameAsc',
  name_desc: 'productFilters.ordering.nameDesc',
};

type DialogState =
  | { type: 'none' }
  | { type: 'createFolder'; parent: LcaProductFolderOutSchema | null }
  | { type: 'renameFolder'; folder: LcaProductFolderOutSchema }
  | { type: 'moveFolder'; folder: LcaProductFolderOutSchema }
  | { type: 'deleteFolder'; folder: LcaProductFolderOutSchema }
  | { type: 'moveProducts'; productIds: number[] }
  | { type: 'deleteProducts'; productIds: number[] };

export default function LCAPageIndexProducts() {
  const { t } = useTranslation('lca');
  const data = [{ name: t('productList'), url: '/lca/products' }];
  const navigate = useNavigate();

  const organization = useLcaOrganization();
  const canWrite = organization.can_write_data;

  const [search, setSearch] = React.useState('');
  const [selectedAreaId, setSelectedAreaId] = React.useState<number | null>(
    null
  );
  const [folder, setFolder] = React.useState<LcaFolderFilter>(undefined);
  const [kind, setKind] = React.useState<LcaProductKind>('all');
  const [ordering, setOrdering] =
    React.useState<LcaProductOrdering>('recently_updated');
  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
  const [dialog, setDialog] = React.useState<DialogState>({ type: 'none' });

  const { page, setPage, elementsPerPage, setElementsPerPage } =
    useElementsPagination();

  const { products, count, isLoading } = useLcaProducts({
    organizationId: organization.id,
    page,
    elementsPerPage,
    name: search,
    folder,
    kind,
    ordering,
  });
  const { folders } = useLcaProductFolders(organization.id);
  const { summary } = useLcaProductFoldersSummary(organization.id);
  const folderActions = useLcaProductFolderActions(organization.id);
  const bulkActions = useLcaProductBulkActions();

  const [newImportDialogOpen, setNewImportDialogOpen] = React.useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = React.useState(false);
  const [uploadExcelDialogOpen, setUploadExcelDialogOpen] =
    React.useState(false);
  // Get emissions data for all products
  const productIds = products.map((product) => product.id);
  const emissionsData = useProductsEmissions(productIds, selectedAreaId);

  const [createMenuAnchorEl, setCreateMenuAnchorEl] =
    React.useState<null | HTMLElement>(null);

  const closeDialog = () => setDialog({ type: 'none' });

  const resetListState = () => {
    setPage(1);
    setSelectedIds([]);
  };

  const handleCreateMenuClick = (e: React.MouseEvent<HTMLButtonElement>) =>
    setCreateMenuAnchorEl(e.currentTarget);

  const handleCreateProductClick = () => {
    navigate('/lca/products/create');
    setCreateMenuAnchorEl(null);
  };

  const handleUploadProductClick = () => {
    setCreateMenuAnchorEl(null);
    setUploadExcelDialogOpen(true);
  };

  const handleNewImportClick = () => {
    setCreateMenuAnchorEl(null);
    setNewImportDialogOpen(true);
  };

  const toggleSelected = (productId: number) =>
    setSelectedIds((current) =>
      current.includes(productId)
        ? current.filter((id) => id !== productId)
        : [...current, productId]
    );

  const allOnPageSelected =
    !!productIds.length && productIds.every((id) => selectedIds.includes(id));

  const toggleSelectAllOnPage = () =>
    setSelectedIds((current) =>
      allOnPageSelected
        ? current.filter((id) => !productIds.includes(id))
        : Array.from(new Set([...current, ...productIds]))
    );

  const folderErrorKey = (error: any) => {
    const status = error?.response?.status;
    return status === 400 || status === 409
      ? 'folders.toasts.duplicateName'
      : 'folders.toasts.error';
  };

  const handleCreateFolder = (name: string, parentId: number | null) => {
    folderActions.createFolder
      .mutateAsync({ name, parentId })
      .then(() => {
        toast.success(t('folders.toasts.created') as string);
        closeDialog();
      })
      .catch((error) => toast.error(t(folderErrorKey(error)) as string));
  };

  const handleRenameFolder = (folderId: number, name: string) => {
    folderActions.renameFolder
      .mutateAsync({ id: folderId, name })
      .then(() => {
        toast.success(t('folders.toasts.renamed') as string);
        closeDialog();
      })
      .catch((error) => toast.error(t(folderErrorKey(error)) as string));
  };

  const handleMoveFolder = (folderId: number, parentId: number | null) => {
    folderActions.moveFolder
      .mutateAsync({ id: folderId, parentId })
      .then(() => {
        toast.success(t('folders.toasts.moved') as string);
        closeDialog();
      })
      .catch((error) => toast.error(t(folderErrorKey(error)) as string));
  };

  const handleDeleteFolder = (target: LcaProductFolderOutSchema) => {
    folderActions.deleteFolder
      .mutateAsync(target.id)
      .then(() => {
        toast.success(t('folders.toasts.deleted') as string);
        if (folder === target.id) {
          // its products moved one level up, so that is where to look for them
          setFolder(target.parent_id ?? undefined);
          resetListState();
        }
        closeDialog();
      })
      .catch((error) => {
        const clashes = getDeleteNameClashes(folders, target);
        toast.error(
          (error?.response?.status === 400 && clashes.length
            ? t('folders.toasts.deleteNameClash', { names: clashes.join(', ') })
            : t('folders.toasts.error')) as string
        );
      });
  };

  // a product filed in a sub-folder of the open folder says which one
  const subfolderLabel = (productFolderId?: number | null) => {
    if (typeof folder !== 'number' || !productFolderId) return undefined;
    if (productFolderId === folder) return undefined;
    const path = getFolderPath(folders, productFolderId);
    const openFolderIndex = path.findIndex((item) => item.id === folder);
    return formatFolderPath(path.slice(openFolderIndex + 1));
  };

  // the folder's contents move one level up, which is "no folder" at the top
  const deleteFolderDescription = () => {
    if (dialog.type !== 'deleteFolder') return undefined;
    const parent = folders.find((item) => item.id === dialog.folder.parent_id);
    return parent
      ? t('folders.deleteDialog.descNested', { parent: parent.name })
      : t('folders.deleteDialog.desc');
  };

  const handleMoveProducts = (
    productIdsToMove: number[],
    folderId: number | null
  ) => {
    bulkActions.moveProducts
      .mutateAsync({ productIds: productIdsToMove, folderId })
      .then(() => {
        toast.success(t('selection.toasts.moved') as string);
        setSelectedIds([]);
        closeDialog();
      })
      .catch(() => toast.error(t('selection.toasts.error') as string));
  };

  const handleDeleteProducts = (productIdsToDelete: number[]) => {
    bulkActions.deleteProducts
      .mutateAsync(productIdsToDelete)
      .then((result) => {
        if (!result.deleted_ids.length) {
          toast.error(t('selection.toasts.allBlocked') as string);
        } else if (result.blocked_ids.length) {
          toast.warn(t('selection.toasts.partiallyBlocked') as string);
        } else {
          toast.success(t('selection.toasts.deleted') as string);
        }
        setSelectedIds([]);
        closeDialog();
      })
      .catch(() => toast.error(t('selection.toasts.error') as string));
  };

  const hasActiveFilters = !!search || folder !== undefined || kind !== 'all';
  const isBulkBusy =
    bulkActions.moveProducts.isLoading || bulkActions.deleteProducts.isLoading;

  return (
    <LcaLayout>
      <Breadcrumbs data={data} />
      <div
        style={{
          marginTop: '44px',
          marginBottom: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h1">{t('products')}</Typography>

        <Box display="flex" gap="8px" alignItems="center">
          <AreaSelector
            selectedAreaId={selectedAreaId}
            onAreaChange={setSelectedAreaId}
          />
          <Button onClick={() => navigate('/lca/help-centre')}>
            <HelpOutlineOutlinedIcon />
            {t('knowledgeBase')}
          </Button>
          <Button onClick={() => setSettingsDialogOpen(true)}>
            <SettingsOutlinedIcon />
            {t('lcaSettings.button')}
          </Button>
          {canWrite && (
            <ButtonGroup>
              <Button onClick={handleCreateProductClick}>
                {t('product.createNew')}
              </Button>
              <Button onClick={handleCreateMenuClick}>
                <ArrowDropDown />
              </Button>
              <Menu
                anchorEl={createMenuAnchorEl}
                open={!!createMenuAnchorEl}
                onClose={() => setCreateMenuAnchorEl(null)}
              >
                <MenuItem onClick={handleNewImportClick}>
                  {t('product.uploadNewFromExcel')}
                </MenuItem>
                <MenuItem onClick={handleUploadProductClick}>
                  {t('product.uploadCopyFromExcel')}
                </MenuItem>
              </Menu>
            </ButtonGroup>
          )}
        </Box>
      </div>
      {canWrite && (
        <Typography variant="subtitle1" color="text.secondary">
          {t('product.createNewInstructions')}
        </Typography>
      )}

      <Box display="flex" gap="24px" alignItems="flex-start" mt="24px">
        <FoldersSidebar
          folders={folders}
          summary={summary}
          selectedFolder={folder}
          canWrite={canWrite}
          onSelect={(nextFolder) => {
            setFolder(nextFolder);
            resetListState();
          }}
          onCreate={() => setDialog({ type: 'createFolder', parent: null })}
          onCreateSubfolder={(parent) =>
            setDialog({ type: 'createFolder', parent })
          }
          onMove={(target) => setDialog({ type: 'moveFolder', folder: target })}
          onRename={(target) =>
            setDialog({ type: 'renameFolder', folder: target })
          }
          onDelete={(target) =>
            setDialog({ type: 'deleteFolder', folder: target })
          }
        />

        <Box flex="1" minWidth={0}>
          {typeof folder === 'number' && (
            <FolderPath
              folders={folders}
              folderId={folder}
              onSelect={(nextFolder) => {
                setFolder(nextFolder);
                resetListState();
              }}
            />
          )}
          <Box
            display="flex"
            gap="16px"
            alignItems="center"
            flexWrap="wrap"
            mb="24px"
          >
            <DelayedTextField
              onDelayedChange={(value: string) => {
                setSearch(value);
                resetListState();
              }}
              placeholder={t('product.search')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ width: '320px' }}
            />

            <ToggleButtonGroup
              exclusive
              value={kind}
              onChange={(_, value) => {
                if (!value) return;
                setKind(value as LcaProductKind);
                resetListState();
              }}
              sx={{
                '& .MuiToggleButton-root': {
                  minWidth: '120px',
                  height: '48px',
                },
              }}
            >
              {PRODUCT_KINDS.map((productKind) => (
                <ToggleButton key={productKind} value={productKind}>
                  {t(KIND_LABEL_KEYS[productKind])}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <Select
              value={ordering}
              onChange={(event) => {
                setOrdering(event.target.value as LcaProductOrdering);
                resetListState();
              }}
              sx={{ minWidth: '220px', height: '48px' }}
            >
              {(Object.keys(ORDERING_LABEL_KEYS) as LcaProductOrdering[]).map(
                (value) => (
                  <SelectItem key={value} value={value}>
                    {t(ORDERING_LABEL_KEYS[value])}
                  </SelectItem>
                )
              )}
            </Select>
          </Box>

          {canWrite && !!selectedIds.length && (
            <ProductSelectionBar
              selectedCount={selectedIds.length}
              allOnPageSelected={allOnPageSelected}
              isBusy={isBulkBusy}
              onToggleSelectAll={toggleSelectAllOnPage}
              onClear={() => setSelectedIds([])}
              onMove={() =>
                setDialog({ type: 'moveProducts', productIds: selectedIds })
              }
              onDelete={() =>
                setDialog({ type: 'deleteProducts', productIds: selectedIds })
              }
            />
          )}

          {isLoading ? (
            <PageLoading />
          ) : (
            <>
              {!count && !hasActiveFilters && (
                <ProductsItemsEmpty canCreate={canWrite} />
              )}
              {!count && hasActiveFilters && (
                <Typography variant="h5" color="text.secondary">
                  {t('product.noResults')}
                </Typography>
              )}
              {!!count && (
                <div>
                  <div
                    style={{
                      display: 'flex',
                      gap: '24px',
                      flexWrap: 'wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {products.map((el) => {
                      const productEmissions = emissionsData[el.id];
                      return (
                        <ProductsItemsCreated
                          key={el.id}
                          id={el.id}
                          name={el.name}
                          passportAccessToken={el.passport_access_token}
                          period={el.data_collection_period}
                          materials={el.materials}
                          last_edited={el.last_updated}
                          url={`/lca/products/${el.id}`}
                          canDelete={canWrite}
                          impactData={productEmissions?.impact || null}
                          isLoadingImpact={productEmissions?.isLoading || false}
                          impactError={productEmissions?.error}
                          imageUrl={el.image_url}
                          folderLabel={subfolderLabel(el.folder_id)}
                          selectable={canWrite}
                          selected={selectedIds.includes(el.id)}
                          onToggleSelected={toggleSelected}
                          onMoveToFolder={
                            canWrite
                              ? (productId) =>
                                  setDialog({
                                    type: 'moveProducts',
                                    productIds: [productId],
                                  })
                              : undefined
                          }
                        />
                      );
                    })}
                  </div>
                  <ElementsPagination
                    totalElements={count}
                    elementsPerPage={elementsPerPage}
                    setElementsPerPage={setElementsPerPage}
                    page={page}
                    setPage={setPage}
                    sx={{ mt: '24px' }}
                  />
                </div>
              )}
            </>
          )}
        </Box>
      </Box>

      <FolderNameDialog
        open={dialog.type === 'createFolder'}
        titleKey={
          dialog.type === 'createFolder' && dialog.parent
            ? 'lca:folders.createSubfolderDialog.title'
            : 'lca:folders.createDialog.title'
        }
        description={
          dialog.type === 'createFolder' && dialog.parent
            ? t('folders.createSubfolderDialog.description', {
                path: formatFolderPath(
                  getFolderPath(folders, dialog.parent.id)
                ),
              })
            : undefined
        }
        submitKey="lca:folders.createDialog.submit"
        isSubmitting={folderActions.createFolder.isLoading}
        onClose={closeDialog}
        onSubmit={(name) => {
          if (dialog.type === 'createFolder') {
            handleCreateFolder(name, dialog.parent?.id ?? null);
          }
        }}
      />
      <FolderNameDialog
        open={dialog.type === 'renameFolder'}
        titleKey="lca:folders.renameDialog.title"
        submitKey="lca:folders.renameDialog.submit"
        initialName={
          dialog.type === 'renameFolder' ? dialog.folder.name : undefined
        }
        isSubmitting={folderActions.renameFolder.isLoading}
        onClose={closeDialog}
        onSubmit={(name) => {
          if (dialog.type === 'renameFolder') {
            handleRenameFolder(dialog.folder.id, name);
          }
        }}
      />
      <ConfirmationModal
        open={dialog.type === 'deleteFolder'}
        titleKey={t('folders.deleteDialog.title')}
        textKey={deleteFolderDescription()}
        trueButtonTextKey={t('folders.deleteDialog.confirm')}
        falseButtonTextKey={t('folders.deleteDialog.cancel')}
        onTrue={() => {
          if (dialog.type === 'deleteFolder') {
            handleDeleteFolder(dialog.folder);
          }
        }}
        onFalse={closeDialog}
        onClose={closeDialog}
      />
      <MoveToFolderDialog
        open={dialog.type === 'moveFolder'}
        folders={folders}
        titleKey="lca:folders.moveDialog.title"
        description={
          dialog.type === 'moveFolder'
            ? t('folders.moveDialog.description', { name: dialog.folder.name })
            : ''
        }
        noFolderLabel={t('folders.moveDialog.topLevel')}
        currentFolderId={
          dialog.type === 'moveFolder' ? dialog.folder.parent_id : undefined
        }
        excludedFolderIds={
          dialog.type === 'moveFolder'
            ? getSubtreeIds(folders, dialog.folder.id)
            : undefined
        }
        isSubmitting={folderActions.moveFolder.isLoading}
        onClose={closeDialog}
        onSubmit={(parentId) => {
          if (dialog.type === 'moveFolder') {
            handleMoveFolder(dialog.folder.id, parentId);
          }
        }}
      />
      <MoveToFolderDialog
        open={dialog.type === 'moveProducts'}
        folders={folders}
        titleKey="lca:selection.moveDialog.title"
        description={`${t('selection.moveDialog.description')} (${t(
          'selection.selected',
          {
            count:
              dialog.type === 'moveProducts' ? dialog.productIds.length : 0,
          }
        )})`}
        noFolderLabel={t('selection.moveDialog.noFolder')}
        isSubmitting={bulkActions.moveProducts.isLoading}
        onClose={closeDialog}
        onSubmit={(folderId) => {
          if (dialog.type === 'moveProducts') {
            handleMoveProducts(dialog.productIds, folderId);
          }
        }}
      />
      <ConfirmationModal
        open={dialog.type === 'deleteProducts'}
        titleKey={t('selection.deleteDialog.title')}
        textKey={t('selection.deleteDialog.desc')}
        trueButtonTextKey={t('selection.deleteDialog.confirm')}
        falseButtonTextKey={t('selection.deleteDialog.cancel')}
        onTrue={() => {
          if (dialog.type === 'deleteProducts') {
            handleDeleteProducts(dialog.productIds);
          }
        }}
        onFalse={closeDialog}
        onClose={closeDialog}
      />

      <ProductExcelUploadDialog
        open={uploadExcelDialogOpen}
        onClose={() => setUploadExcelDialogOpen(false)}
      />
      <NewProductExcelImportDialog
        open={newImportDialogOpen}
        onClose={() => setNewImportDialogOpen(false)}
      />
      <LcaSettingsDialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
      />
    </LcaLayout>
  );
}
